import { useState, useEffect } from "react";
import axios from "axios";
import {
  TbUsers, TbPlus, TbRefresh, TbSearch,
  TbAlertCircle, TbEdit, TbTrash, TbCheck,
  TbX, TbBuilding, TbPhone, TbMail, TbCreditCard,
  TbChevronDown, TbChevronUp
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";

function OwnerForm({ initial = {}, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    name:           initial.name           || "",
    email:          initial.email          || "",
    phone:          initial.phone          || "",
    iban:           initial.iban           || "",
    commissionRate: initial.commissionRate ?? 20,
    notes:          initial.notes          || "",
  });

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Nom *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ahmed Ousseni"
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Email</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="ahmed@email.com"
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Téléphone</label>
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+269 321 00 00"
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Commission agence (%)</label>
          <input
            name="commissionRate"
            type="number"
            min="0"
            max="100"
            value={form.commissionRate}
            onChange={handleChange}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white"
          />
        </div>
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">IBAN</label>
          <input
            name="iban"
            value={form.iban}
            onChange={handleChange}
            placeholder="KM00 0000 0000 0000 0000 0000"
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white font-mono"
          />
        </div>
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Notes internes</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Notes visibles uniquement par l'agence..."
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition bg-white resize-none"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end pt-1">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-100 transition"
        >
          Annuler
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={loading || !form.name.trim()}
          className="px-4 py-2 text-sm bg-[#3A9D23] text-white rounded-xl hover:bg-[#2d8019] transition disabled:opacity-60 flex items-center gap-1.5"
        >
          <TbCheck size={15} />
          {loading ? "Enregistrement..." : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

function HotelsDrawer({ owner, hotels, onClose, onSave, saving }) {
  const assigned = hotels.filter(h => h.ownerId?.toString() === owner._id);
  const unassigned = hotels.filter(h => !h.ownerId || h.ownerId?.toString() !== owner._id);

  const [toAssign, setToAssign]     = useState([]);
  const [toUnassign, setToUnassign] = useState([]);

  const toggleAssign = (id) => {
    setToAssign(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);
  };
  const toggleUnassign = (id) => {
    setToUnassign(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-end">
      <div className="w-full max-w-md h-full bg-white shadow-xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">Biens de {owner.name}</p>
            <p className="text-xs text-gray-400 mt-0.5">Associer ou dissocier des biens</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400">
            <TbX size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

          {/* Biens assignés */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Biens assignés ({assigned.length})
            </p>
            {assigned.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Aucun bien assigné</p>
            ) : (
              <div className="flex flex-col gap-2">
                {assigned.map(h => (
                  <div
                    key={h._id}
                    onClick={() => toggleUnassign(h._id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                      toUnassign.includes(h._id)
                        ? "border-red-200 bg-red-50"
                        : "border-gray-100 hover:border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#eaf3de] flex items-center justify-center shrink-0">
                      <TbBuilding className="text-[#3A9D23]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{h.name}</p>
                      <p className="text-xs text-gray-400">{h.city}, {h.island}</p>
                    </div>
                    {toUnassign.includes(h._id) && (
                      <span className="text-xs text-red-500 font-medium shrink-0">Retirer</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Biens disponibles */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Biens disponibles ({unassigned.length})
            </p>
            {unassigned.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Tous les biens sont assignés</p>
            ) : (
              <div className="flex flex-col gap-2">
                {unassigned.map(h => (
                  <div
                    key={h._id}
                    onClick={() => toggleAssign(h._id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                      toAssign.includes(h._id)
                        ? "border-emerald-200 bg-emerald-50"
                        : "border-gray-100 hover:border-gray-200 bg-white"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <TbBuilding className="text-gray-400" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{h.name}</p>
                      <p className="text-xs text-gray-400">{h.city}, {h.island}</p>
                    </div>
                    {toAssign.includes(h._id) && (
                      <span className="text-xs text-emerald-600 font-medium shrink-0">Ajouter</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-sm text-gray-500 rounded-xl hover:bg-gray-50 transition">
            Annuler
          </button>
          <button
            onClick={() => onSave({ toAssign, toUnassign })}
            disabled={saving || (toAssign.length === 0 && toUnassign.length === 0)}
            className="flex-1 py-2.5 bg-[#3A9D23] text-white text-sm rounded-xl hover:bg-[#2d8019] transition disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : `Appliquer (${toAssign.length + toUnassign.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnerCard({ owner, hotels, onEdit, onDelete, onAssignHotels }) {
  const [expanded, setExpanded] = useState(false);
  const assignedHotels = hotels.filter(h => h.ownerId?.toString() === owner._id);

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

      {/* Header card */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 rounded-full bg-[#eaf3de] text-[#3B6D11] flex items-center justify-center text-sm font-medium shrink-0">
          {owner.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{owner.name}</p>
          <p className="text-xs text-gray-400">
            Commission : <span className="font-medium text-gray-600">{owner.commissionRate}%</span>
            {" · "}
            <span>{assignedHotels.length} bien{assignedHotels.length > 1 ? "s" : ""}</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onAssignHotels(owner)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-[#3A9D23]"
            title="Gérer les biens"
          >
            <TbBuilding size={16} />
          </button>
          <button
            onClick={() => onEdit(owner)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-blue-500"
          >
            <TbEdit size={16} />
          </button>
          <button
            onClick={() => onDelete(owner)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition text-gray-400 hover:text-red-500"
          >
            <TbTrash size={16} />
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-400"
          >
            {expanded ? <TbChevronUp size={16} /> : <TbChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Détail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-3 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: TbMail,       label: "Email",    value: owner.email || "—" },
              { icon: TbPhone,      label: "Téléphone",value: owner.phone || "—" },
              { icon: TbCreditCard, label: "IBAN",     value: owner.iban  || "—", mono: true },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className={`bg-gray-50 rounded-xl p-3 ${i === 2 ? "col-span-2" : ""}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={12} className="text-gray-400" />
                    <p className="text-xs text-gray-400">{item.label}</p>
                  </div>
                  <p className={`text-xs font-medium text-gray-800 ${item.mono ? "font-mono" : ""}`}>
                    {item.value}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Biens assignés */}
          {assignedHotels.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-2">Biens associés</p>
              <div className="flex flex-wrap gap-2">
                {assignedHotels.map(h => (
                  <span key={h._id} className="flex items-center gap-1 px-2 py-1 bg-[#eaf3de] text-[#3B6D11] rounded-lg text-xs font-medium">
                    <TbBuilding size={11} />
                    {h.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {owner.notes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
              <p className="text-xs text-amber-600 font-medium mb-0.5">Note interne</p>
              <p className="text-xs text-amber-800">{owner.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgencyOwners() {
  const [owners, setOwners]   = useState([]);
  const [hotels, setHotels]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState("");

  const [showForm, setShowForm]       = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [hotelsDrawer, setHotelsDrawer] = useState(null);
  const [drawerSaving, setDrawerSaving] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/owners`, { withCredentials: true }),
      axios.get(`${API}`,        { withCredentials: true }),
    ])
      .then(([ownersRes, hotelsRes]) => {
        setOwners(ownersRes.data);
        setHotels(hotelsRes.data);
      })
      .catch(() => setError("Impossible de charger les propriétaires."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (form) => {
    setFormLoading(true);
    try {
      const res = await axios.post(`${API}/owners`, form, { withCredentials: true });
      setOwners(o => [res.data, ...o]);
      setShowForm(false);
    } catch {
      alert("Erreur lors de la création.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (form) => {
    setFormLoading(true);
    try {
      const res = await axios.put(`${API}/owners/${editingOwner._id}`, form, { withCredentials: true });
      setOwners(o => o.map(x => x._id === editingOwner._id ? res.data : x));
      setEditingOwner(null);
    } catch {
      alert("Erreur lors de la mise à jour.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (owner) => {
    if (!window.confirm(`Supprimer ${owner.name} ? Ses biens seront dissociés.`)) return;
    try {
      await axios.delete(`${API}/owners/${owner._id}`, { withCredentials: true });
      setOwners(o => o.filter(x => x._id !== owner._id));
      setHotels(h => h.map(x => x.ownerId?.toString() === owner._id ? { ...x, ownerId: null } : x));
    } catch {
      alert("Erreur lors de la suppression.");
    }
  };

  const handleAssignSave = async ({ toAssign, toUnassign }) => {
    setDrawerSaving(true);
    try {
      await axios.patch(
        `${API}/owners/${hotelsDrawer._id}/hotels`,
        { toAssign, toUnassign },
        { withCredentials: true }
      );
      // Mettre à jour les hotels localement
      setHotels(h => h.map(hotel => {
        if (toAssign.includes(hotel._id))   return { ...hotel, ownerId: hotelsDrawer._id };
        if (toUnassign.includes(hotel._id)) return { ...hotel, ownerId: null };
        return hotel;
      }));
      setHotelsDrawer(null);
    } catch {
      alert("Erreur lors de la mise à jour des biens.");
    } finally {
      setDrawerSaving(false);
    }
  };

  const filtered = owners.filter(o =>
    o.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Propriétaires</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {owners.length} propriétaire{owners.length > 1 ? "s" : ""} enregistré{owners.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} className="w-9 h-9 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400">
            <TbRefresh size={16} />
          </button>
          <button
            onClick={() => { setShowForm(true); setEditingOwner(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#3A9D23] text-white text-sm font-medium rounded-xl hover:bg-[#2d8019] transition shadow-sm"
          >
            <TbPlus size={16} />
            Ajouter un propriétaire
          </button>
        </div>
      </div>

      {/* ── RECHERCHE ── */}
      <div className="relative max-w-sm">
        <TbSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un propriétaire..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[#3A9D23] focus:ring-2 focus:ring-[#3A9D23]/10 transition"
        />
      </div>

      {/* ── FORM CRÉATION ── */}
      {showForm && !editingOwner && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nouveau propriétaire</p>
          <OwnerForm
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      {/* ── CONTENU ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-gray-400">Chargement...</p>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <TbAlertCircle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
            <TbUsers className="text-gray-400" size={24} />
          </div>
          <p className="text-sm font-medium text-gray-500">
            {search ? "Aucun propriétaire trouvé" : "Aucun propriétaire enregistré"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(owner => (
            editingOwner?._id === owner._id ? (
              <div key={owner._id} className="sm:col-span-2 lg:col-span-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Modifier — {owner.name}
                </p>
                <OwnerForm
                  initial={owner}
                  onSave={handleUpdate}
                  onCancel={() => setEditingOwner(null)}
                  loading={formLoading}
                />
              </div>
            ) : (
              <OwnerCard
                key={owner._id}
                owner={owner}
                hotels={hotels}
                onEdit={setEditingOwner}
                onDelete={handleDelete}
                onAssignHotels={setHotelsDrawer}
              />
            )
          ))}
        </div>
      )}

      {/* ── DRAWER BIENS ── */}
      {hotelsDrawer && (
        <HotelsDrawer
          owner={hotelsDrawer}
          hotels={hotels}
          onClose={() => setHotelsDrawer(null)}
          onSave={handleAssignSave}
          saving={drawerSaving}
        />
      )}
    </div>
  );
}